import { findSuitablePrices } from './member-limits';
import productUtils from './ProductUtils';

/**
 * @typedef {import('./check-limits').LimitValidation} LimitValidation
 * @typedef {import('./ProductUtils').Addon} Addon
 * @typedef {import('./ProductUtils').Limit} Limit
 * @typedef {import('./ProductUtils').PlanBlocker} PlanBlocker
 * @typedef {import('./ProductUtils').Price} Price
 */

/**
 * @typedef {object} EligibilityProduct
 * @property {string} name
 * @property {number} [active]
 * @property {number} [order]
 * @property {Price[]} [prices]
 * @property {Limit[]} [limits]
 * @property {Addon[]} [addons]
 */

/**
 * @typedef {object} ProductEligibility
 * @property {Price[]} suitablePrices
 * @property {Price|null} suitablePrice
 * @property {LimitValidation[]} limitsValidations
 * @property {object[]} availableAddonPrices
 * @property {PlanBlocker[]} blockers
 * @property {boolean} disableProduct
 * @property {string|null} validationMsg
 */

/**
 * Evaluates whether one product can be selected for the current site.
 *
 * Callers without site usage retain the legacy audience-only behavior: the
 * suitable price is resolved, while usage and structural blockers are skipped.
 *
 * @param {object} options
 * @param {EligibilityProduct} options.product
 * @param {EligibilityProduct[]} options.availableProducts
 * @param {number} options.currentMembers
 * @param {object|null} options.currentSiteLimits
 * @param {'month'|'year'} options.showPeriod
 * @param {{base_product?: string, stripe_price_id?: string}|null} options.currentPrice
 * @param {boolean} [options.isSubdirectory=false]
 * @param {{major: number}|null} [options.currentGhostVersion=null]
 * @param {boolean} [options.hasCustomSendingDomain=false]
 * @returns {ProductEligibility}
 */
export function evaluateProductEligibility({
    product,
    availableProducts,
    currentMembers,
    currentSiteLimits,
    showPeriod,
    currentPrice,
    isSubdirectory = false,
    currentGhostVersion = null,
    hasCustomSendingDomain = false,
}) {
    const prices = product?.prices || [];
    const suitablePrices = prices.length
        ? findSuitablePrices(prices, currentMembers, showPeriod)
        : [];
    const suitablePrice = suitablePrices[0] || null;

    if (!currentSiteLimits) {
        return {
            suitablePrices,
            suitablePrice,
            limitsValidations: [],
            availableAddonPrices: [],
            blockers: [],
            disableProduct: false,
            validationMsg: null,
        };
    }

    const { limitsValidations } = productUtils.getPriceAndLimits({
        suitablePrices,
        currentSiteLimits,
        productName: product.name,
        limits: product.limits,
    });
    const availableAddonPrices = product?.addons?.length
        ? productUtils.processAddons({
              addons: product.addons,
              isSubdirectory,
              showPeriod,
          })
        : [];
    const { blockers, disableProduct, validationMsg } = productUtils.shouldDisableProduct({
        suitablePrice,
        currentPrice,
        prices,
        showPeriod,
        isSubdirectory,
        currentGhostVersion,
        productName: product.name,
        limitsValidations,
        availableAddonPrices,
        hasCustomSendingDomain,
        availableProducts,
    });

    return {
        suitablePrices,
        suitablePrice,
        limitsValidations,
        availableAddonPrices,
        blockers,
        disableProduct,
        validationMsg,
    };
}
